import { observer } from "mobx-react-lite";
import { CardProps, Card, Theme, alpha, lighten, Box, SvgIcon, Typography, Button, Alert, useTheme } from "@mui/material";
import React, { createContext } from "react";
import { MdCheckCircle, MdOutlineStorage } from "react-icons/md";
import { ActionConfig } from "../actions/CommonActions";
import { QuickTipButton } from "../../modules/help/HelpCommon";
import { StepperStore } from "../stepper/StepperState";

// ======================
// SelectableBox
// ======================

type SelectableBoxBackground = "light" | "dark" | "transparent";

const getBackgroundColor = (bg: SelectableBoxBackground, selected: boolean, theme: Theme) => {
    const base = (() => {
        switch (bg) {
            case "dark":
                return theme.palette.cirrus.medium;
            case "light":
                return theme.palette.cirrus.light;
            case "transparent":
            default:
                return "rgba(0,0,0,0)";
        }
    })();
    if (!selected) return base;
    const overlay = alpha(theme.palette.primary.main, 0.1);
    return `linear-gradient(${overlay}, ${overlay}), linear-gradient(${base}, ${base})`;
};

interface SelectableBoxProps {
    selected: boolean;
    onSelect?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    disabled?: boolean;
    disableHoverEffects?: boolean;
    darkBorder?: boolean;
    checkmark?: boolean;
    cardProps?: Partial<CardProps>;
    children?: React.ReactNode;
    expandOnSelect?: boolean;
    onDeselect?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    background?: SelectableBoxBackground;
    outline?: boolean;
}

export const SelectableBox: React.FC<SelectableBoxProps> = observer((p) => {
    const {
        selected,
        onSelect,
        onDeselect,
        disabled,
        darkBorder,
        checkmark,
        disableHoverEffects,
        cardProps,
        children,
        background = "transparent",
        outline,
    } = p;
    const theme = useTheme();

    return (
        <SelectableBoxSelectedContext.Provider value={selected}>
            <Card
                {...cardProps}
                sx={{
                    background: getBackgroundColor(background, selected, theme),
                    overflow: "visible",
                    transition: "all 0.25s ease",
                    ...(outline
                        ? {
                              border: "1px solid",
                              borderColor: (t: Theme) => (selected ? t.palette.primary.main : darkBorder ? "rgba(255, 255, 255, .2)" : alpha("#fff", 0.2)),
                          }
                        : (background === "light" || background === "dark") && selected
                        ? {
                              border: "1px solid",
                              borderColor: theme.palette.primary.main,
                          }
                        : {
                              border: "none",
                          }),
                    "&:hover":
                        disabled || p.disableHoverEffects || selected
                            ? {}
                            : background === "light"
                            ? {
                                  backgroundColor: (t: Theme) => lighten(t.palette.cirrus.light, 0.1),
                                  ...(outline ? { borderColor: (t: Theme) => t.palette.primary.main } : {}),
                              }
                            : {
                                  backgroundColor: (t: Theme) => alpha(t.palette.primary.main, 0.1),
                                  ...(outline ? { borderColor: (t: Theme) => t.palette.primary.main } : {}),
                              },
                    "&:active":
                        disabled || disableHoverEffects || selected
                            ? {}
                            : background === "light"
                            ? {
                                  backgroundColor: (t: Theme) => lighten(t.palette.cirrus.light, 0.15),
                                  ...(outline ? { borderColor: (t: Theme) => t.palette.primary.main } : {}),
                              }
                            : {
                                  backgroundColor: (t: Theme) => alpha(t.palette.primary.main, 0.2),
                                  ...(outline ? { borderColor: (t: Theme) => t.palette.primary.main } : {}),
                              },
                    ...cardProps?.sx,
                    cursor: disabled ? "default" : "pointer",
                }}
                elevation={0}
                onClick={disabled || selected ? () => {} : onSelect}
            >
                <Box height={"100%"} display={"flex"} flexDirection={"column"} justifyContent={"space-between"}>
                    <Box
                        onClick={(e) => {
                            if (selected && !!onDeselect) {
                                onDeselect(e);
                            }
                        }}
                        sx={{
                            cursor: disabled ? "default" : "pointer",
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {children}
                    </Box>
                    {!!checkmark && (
                        <Box display={"flex"} justifyContent={"center"} pb={4}>
                            <SvgIcon
                                sx={{
                                    color: selected ? (t: Theme) => t.palette.primary.main : "rgba(255, 255, 255, .2)",
                                }}
                            >
                                <MdCheckCircle />
                            </SvgIcon>
                        </Box>
                    )}
                </Box>
            </Card>
        </SelectableBoxSelectedContext.Provider>
    );
});

// ======================
// SelectableCard
// ======================

export interface SelectableCardProps extends SelectableBoxProps {
    icon?: React.ReactNode;
    title?: string | React.ReactNode;
    description?: string | React.ReactNode;
    tip?: React.ReactNode;
    actions?: ActionConfig[];
    warning?: string;
    chipLabel?: React.ReactNode;
    alert?: string;
    checkmark?: boolean;
    darkBorder?: boolean;
    header?: string | React.ReactNode;
    mainContentPadding?: number;
    compact?: boolean;
}

export const SelectableCard: React.FC<SelectableCardProps> = observer((p) => {
    const {
        cardProps,
        mainContentPadding,
        selected,
        onSelect,
        actions,
        disabled,
        tip,
        warning,
        description,
        title,
        icon,
        chipLabel,
        alert,
        checkmark,
        darkBorder,
        header,
        disableHoverEffects,
        compact,
        background,
        outline,
        ...props
    } = p;

    const theme = useTheme();

    return (
        <SelectableBox
            onSelect={onSelect}
            selected={selected}
            disabled={disabled}
            darkBorder={darkBorder}
            checkmark={checkmark}
            cardProps={cardProps}
            disableHoverEffects={disableHoverEffects}
            background={background}
            outline={outline}
        >
            <Box display={"flex"} flexDirection={"column"} justifyContent={header ? "flex-start" : "center"} height={"100%"}>
                {chipLabel && (
                    <Box p={2} sx={{ opacity: disabled ? 0.2 : 1 }}>
                        {chipLabel}
                    </Box>
                )}
                {header && (
                    <Box
                        sx={
                            selected
                                ? {
                                      backgroundColor: theme.palette.primary.main,
                                      borderTopLeftRadius: 5,
                                      borderTopRightRadius: 5,
                                      border: `1px solid ${theme.palette.primary.main}`,
                                  }
                                : {
                                      backgroundColor: theme.palette.cirrus.light,
                                      borderTopLeftRadius: 5,
                                      borderTopRightRadius: 5,
                                      borderBottom: `2px solid ${alpha("#fff", 0.1)}`,
                                  }
                        }
                        p={1}
                    >
                        <Typography textAlign={"center"}>{header}</Typography>
                    </Box>
                )}

                {compact ? (
                    <Box display="flex" alignItems="center" gap={2} px={2.5} py={2} flexGrow={1}>
                        {icon && <Box sx={{ opacity: disabled ? 0.1 : 1, fontSize: 28, display: "flex", alignItems: "center", flexShrink: 0 }}>{icon}</Box>}
                        <Box flex={1} minWidth={0}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight={600} sx={{ color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : "white") }}>
                                    {title}
                                </Typography>
                                {tip && <QuickTipButton title={tip} sx={{ opacity: disabled ? 0.2 : 1 }} disabled={disabled} />}
                            </Box>
                            {description && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : t.palette.text.secondary), lineHeight: 1.5 }}
                                >
                                    {description}
                                </Typography>
                            )}
                            {warning && (
                                <Typography variant="caption" sx={{ color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : t.palette.warning.main) }}>
                                    {warning}
                                </Typography>
                            )}
                        </Box>
                        {actions && (
                            <Box sx={{ opacity: disabled ? 0.2 : 1, flexShrink: 0 }}>
                                {actions.map((a) => (
                                    <Button
                                        {...a.buttonProps}
                                        key={a.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            a.action();
                                        }}
                                        disabled={disabled || a.disabled}
                                        size="small"
                                    >
                                        {a.name}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        p={mainContentPadding !== undefined ? mainContentPadding : 4}
                        flexGrow={1}
                    >
                        <Box textAlign={"center"}>
                            <Box sx={{ opacity: disabled ? 0.1 : 1 }}>{icon}</Box>
                            <Box p={2}>
                                <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                    <Typography
                                        variant={"h6"}
                                        sx={{
                                            color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : "white"),
                                        }}
                                    >
                                        {p.title}
                                    </Typography>
                                    {tip && (
                                        <>
                                            <Typography>&nbsp; &nbsp;</Typography>
                                            <QuickTipButton title={tip} sx={{ opacity: disabled ? 0.2 : 1 }} disabled={disabled} />
                                        </>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : t.palette.text.secondary),
                                    }}
                                >
                                    {description}
                                </Box>
                            </Box>
                            {actions && (
                                <Box sx={{ opacity: disabled ? 0.2 : 1 }}>
                                    {actions.map((a) => (
                                        <Button
                                            {...a.buttonProps}
                                            key={a.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                a.action();
                                            }}
                                            disabled={disabled || a.disabled}
                                        >
                                            {a.name}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                            {warning && (
                                <Box pt={2}>
                                    <Typography sx={{ color: (t: Theme) => (disabled ? "rgba(255, 255, 255, .2)" : t.palette.warning.main) }}>
                                        {warning}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
                {alert && (
                    <Box p={2}>
                        <Alert severity={"info"}>{alert}</Alert>
                    </Box>
                )}
            </Box>
        </SelectableBox>
    );
});

// ======================
// SelectableCardExpansionContent
// ======================

interface SelectableBoxExpansionContentProps {
    children: React.ReactNode;
}

export const SelectableBoxExpansionContent: React.FC<SelectableBoxExpansionContentProps> = (p) => {
    const { children } = p;
    return (
        <SelectableBoxSelectedContext.Consumer>
            {(selected) => {
                if (selected) {
                    return (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            {children}
                        </Box>
                    );
                }
                return null;
            }}
        </SelectableBoxSelectedContext.Consumer>
    );
};

export const SelectableBoxSelectedContext = createContext<boolean>(false);
